import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import Add from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useGetIdentity } from "react-admin";
import { useUserList, type StatusFilter } from "./useUserList";
import { useRoleOptions } from "./useRoleOptions";
import { UserDataGrid } from "./UserDataGrid";
import { IfPermission } from "../../auth/IfPermission";
import type { CurrentUserHierarchy } from "../../auth/UserHierarchy";
import type { JSX } from "react";

export function UserList() {
  let userList: JSX.Element;
  const navigate = useNavigate();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const { state, fetching, search, roleId, status, offset, limit, setSearch, setRoleId, setStatus, setOffset, setLimit, refetch } =
    useUserList();
  const roleOptions = useRoleOptions();
  // MUI считает страницы с нуля, экран — с единицы.
  const zeroBasedPage = Math.floor(offset / limit);

  if (identityLoading || identity === undefined) {
    userList = (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  } else {
    const current: CurrentUserHierarchy = {
      id: String(identity.id),
      level: Number(identity.level),
      roleType: identity.roleType === "system" ? "system" : "user",
    };
    userList = (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Пользователи</Typography>
        <IfPermission permission="users.create">
          <Button variant="contained" startIcon={<Add />} onClick={() => {
      void navigate("/users/create");
    }}>
            Создать пользователя
          </Button>
        </IfPermission>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <Box flex={2} minWidth={0}>
          <TextField fullWidth
            label="Поиск по логину и имени"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="small"
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <TextField fullWidth
            select
            label="Роль"
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
            size="small"
          >
            <MenuItem value="">Все роли</MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box flex={1} minWidth={0}>
          <TextField fullWidth
            select
            label="Статус"
            value={status}
            onChange={(event) => setStatus(event.target.value as StatusFilter)}
            size="small"
          >
            <MenuItem value="">Все статусы</MenuItem>
            <MenuItem value="active">Активен</MenuItem>
            <MenuItem value="inactive">Деактивирован</MenuItem>
          </TextField>
        </Box>
      </Box>

      {fetching && state.kind !== "loading" ? <LinearProgress sx={{ mb: 1 }} /> : null}

      {state.kind === "loading" ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : null}

      {state.kind === "error" ? (
        <Alert severity="error" action={<Button onClick={refetch}>Повторить</Button>}>
          Не удалось загрузить пользователей: {state.message}
        </Alert>
      ) : null}

      {state.kind === "empty" ? <Typography color="text.secondary">Пользователи не найдены.</Typography> : null}

      {state.kind === "data" ? (
        <>
          <UserDataGrid items={state.items} current={current} onEdit={(user) => {
      void navigate(`/users/${user.id}`);
    }} />
          <TablePagination
            component="div"
            count={state.totalCount}
            page={zeroBasedPage}
            rowsPerPage={limit}
            onPageChange={(_, page) => setOffset(page * limit)}
            onRowsPerPageChange={(event) => setLimit(Number(event.target.value))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Строк на странице"
          />
        </>
      ) : null}
    </Box>
  );
  }
  return userList;
}
